import React, { useEffect, useState } from 'react';
import { Navigate, Outlet, useLocation } from 'react-router-dom';
import Launchbar from './Launchbar';
import { useBaseUrl } from '../utils/utils';
import { restoreModernSession } from '../utils/sessionAuth';
import Loading from '../institutions/dashboard_pages/Loading';

const publicInstitutionPaths = new Set([
    '/',
    '/institution/newlog',
    '/institution/newreg',
    '/institution/register',
    '/institution/start',
    '/institution/create',
    '/institution/login',
    '/patient/start',
    '/patient/login',
    '/partner/start',
    '/partner/login',
    '/admin/start',
]);

const requiresStaffSession = (pathname: string) => {
    if (pathname.startsWith('/institution')) {
        return !publicInstitutionPaths.has(pathname);
    }

    if (pathname.startsWith('/partner')) {
        return !publicInstitutionPaths.has(pathname);
    }

    return false;
};

const AuthShell: React.FC = () => {
    const location = useLocation();
    const baseUrl = useBaseUrl();
    const [allowed, setAllowed] = useState<boolean | null>(null);
    const [checking, setChecking] = useState(false);
    const [hasProtectedAccess, setHasProtectedAccess] = useState(false);

    useEffect(() => {
        let mounted = true;
        const needsStaffSession = requiresStaffSession(location.pathname);

        if (!needsStaffSession) {
            setAllowed(true);
            setChecking(false);
            return () => {
                mounted = false;
            };
        }

        setChecking(true);
        if (!hasProtectedAccess) {
            setAllowed(null);
        }

        restoreModernSession(baseUrl)
            .then((session) => {
                if (!mounted) return;
                const isStaff = session?.user?.role === 'staff';
                setAllowed(isStaff);
                setHasProtectedAccess(isStaff);
                setChecking(false);
            })
            .catch(() => {
                if (!mounted) return;
                setAllowed(false);
                setHasProtectedAccess(false);
                setChecking(false);
            });

        return () => {
            mounted = false;
        };
    }, [baseUrl, location.pathname]);

    if (allowed === null) {
        return <Loading />;
    }

    if (!allowed) {
        return <Navigate to="/institution/newlog" state={{ from: location }} replace />;
    }

    return (
        <>
            {checking && requiresStaffSession(location.pathname) && (
                <div
                    style={{
                        position: 'fixed',
                        top: 0,
                        left: 0,
                        right: 0,
                        height: '3px',
                        zIndex: 2000,
                        background: 'linear-gradient(90deg, #0ab39c 0%, #405189 100%)',
                        boxShadow: '0 1px 6px rgba(0,0,0,0.12)',
                    }}
                />
            )}
            <Outlet />
            <Launchbar />
        </>
    );
};

export default AuthShell;
